import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, useHistory } from "react-router-dom";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import moment from "moment";
import useAuthentication from "../../hooks/useAuthentication";
import WeClappCustomerSelector from "../WeClapp/WeClappCustomerSelector";
import SlideToConfirm from "../UI/SlideToConfirm";

const InvoicesPage = () => {
    const { id } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [invoices, setInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [showDetail, setShowDetail] = useState(false);
    const [weclappCustomerId, setWeclappCustomerId] = useState("5663644");
    const [weclappCustomerName, setWeclappCustomerName] = useState("");
    const [activeTab, setActiveTab] = useState('invoices');
    const [openServiceProjects, setOpenServiceProjects] = useState([]);
    const [customerFilter, setCustomerFilter] = useState(null);
    const [showCustomerSelector, setShowCustomerSelector] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
    const [filterDescription, setFilterDescription] = useState('');
    const [filterStatus, setFilterStatus] = useState(''); // 'local' or 'weclapp'
    const [filterCreator, setFilterCreator] = useState('');
    const [sendingNotifications, setSendingNotifications] = useState(false);
    const [notificationResult, setNotificationResult] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const Auth = useAuthentication();
    const Team = useSelector((state) => state.Team);
    const CurrentUser = useSelector((state) => state.CurrentUser);

    useEffect(() => {
        // Check for customer filter in URL query params
        const searchParams = new URLSearchParams(location.search);
        const customerId = searchParams.get('customer');
        if (customerId) {
            setCustomerFilter(parseInt(customerId));
        }
    }, [location.search]);

    useEffect(() => {
        if (activeTab === 'invoices') {
            loadInvoices();
        } else if (activeTab === 'service') {
            loadOpenServiceProjects();
        }
    }, [activeTab]);

    useEffect(() => {
        if (id) {
            loadInvoiceDetail(id);
        }
    }, [id]);

    useEffect(() => {
        // Filter invoices
        let filtered = invoices;

        if (customerFilter) {
            filtered = filtered.filter(inv => inv.customer == customerFilter);
        }

        if (filterDescription) {
            filtered = filtered.filter(inv =>
                inv.description.toLowerCase().includes(filterDescription.toLowerCase()) ||
                (inv.customer_name && inv.customer_name.toLowerCase().includes(filterDescription.toLowerCase()))
            );
        }

        if (filterStatus === 'local') {
            filtered = filtered.filter(inv => !inv.weclapp_invoice_id || inv.weclapp_invoice_id == 0);
        } else if (filterStatus === 'weclapp') {
            filtered = filtered.filter(inv => inv.weclapp_invoice_id > 0);
        }

        if (filterCreator) {
            filtered = filtered.filter(inv => inv.created_by === filterCreator);
        }

        setFilteredInvoices(filtered);
    }, [invoices, customerFilter, filterDescription, filterStatus, filterCreator]);

    const resetFilters = () => {
        setFilterDescription('');
        setFilterStatus('');
        setFilterCreator('');
        setCustomerFilter(null);
    };

    // Get unique creators
    const uniqueCreators = [...new Set(invoices.map(inv => inv.created_by).filter(Boolean))].sort();

    const loadInvoices = () => {
        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "invoices",
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setInvoices(response.data.data);
            }
            setLoading(false);
        }).catch(error => {
            console.error("Error loading invoices:", error);
            setLoading(false);
        });
    };

    const loadInvoiceDetail = (invoiceId) => {
        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "invoice?id=" + invoiceId,
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                const invoice = response.data.data;
                setSelectedInvoice(invoice);
                
                // Pre-fill WeClapp customer ID if available
                if (invoice.customer_weclapp_id) {
                    setWeclappCustomerId(invoice.customer_weclapp_id);
                    // Set customer name from invoice
                    if (invoice.customer_name) {
                        setWeclappCustomerName(invoice.customer_name);
                    }
                }
                
                setShowDetail(true);
            }
        }).catch(error => {
            console.error("Error loading invoice detail:", error);
        });
    };

    const sendToWeClapp = () => {
        if (!weclappCustomerId) {
            alert("Bitte Kunden-ID eingeben!");
            return;
        }

        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "invoice/send_to_weclapp",
            auth: Auth.getAuthObject(),
            data: {
                invoiceId: selectedInvoice.id,
                customerId: weclappCustomerId,
                articleId: "1916"
            }
        }).then(response => {
            if (response.data.success) {
                alert(`Rechnung wurde erfolgreich an WeClapp gesendet! WeClapp-ID: ${response.data.weclappData.id}`);
                setShowDetail(false);
                loadInvoices();
            } else {
                alert("Fehler: " + response.data.error);
            }
        }).catch(error => {
            console.error("Error sending to WeClapp:", error);
            alert("Fehler beim Senden an WeClapp: " + error.message);
        });
    };

    const formatMinutes = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours}:${mins.toString().padStart(2, '0')} h`;
    };

    const handleCustomerSelect = (customer) => {
        setWeclappCustomerId(customer.id);
        setWeclappCustomerName(customer.company || customer.name || "");
        setShowCustomerSelector(false);
    };
    
    const deleteInvoice = () => {
        if (!selectedInvoice) return;
        
        axios({
            method: "delete",
            url: SyncConstants["syncBaseURL"] + "invoice?id=" + selectedInvoice.id,
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                alert("Rechnung wurde erfolgreich gelöscht und die Zeiteinträge wurden freigegeben.");
                setShowDeleteConfirm(false);
                setShowDetail(false);
                setSelectedInvoice(null);
                loadInvoices();
            } else {
                alert("Fehler beim Löschen: " + response.data.error);
            }
        }).catch(error => {
            console.error("Error deleting invoice:", error);
            alert("Fehler beim Löschen der Rechnung: " + error.message);
        });
    };
    
    const getRealNameFromUsername = (username) => {
        if (!username) return '-';
        
        // Team state stores users directly as properties
        if (Team && Team[username]) {
            return Team[username].realName || username;
        }
        
        return username;
    };

    // Helper to get VK price from external cost (from metadata.vk_total or fallback to amount)
    const getExternalCostVK = (cost) => {
        // vk_total is now calculated by the backend using the centralized price logic
        if (cost.vk_total !== undefined && cost.vk_total !== null) {
            return parseFloat(cost.vk_total);
        }
        if (cost.override_vk_price !== undefined && cost.override_vk_price !== null) {
            return parseFloat(cost.override_vk_price);
        }
        if (cost.metadata) {
            try {
                const meta = typeof cost.metadata === 'string' ? JSON.parse(cost.metadata) : cost.metadata;
                if (meta.configured_vk_price !== undefined) return parseFloat(meta.configured_vk_price);
                if (meta.vk_total !== undefined) return parseFloat(meta.vk_total);
            } catch (e) {}
        }
        return parseFloat(cost.amount || 0);
    };

    const loadOpenServiceProjects = () => {
        setLoading(true);
        axios({
            method: "get",
            url: SyncConstants["syncBaseURL"] + "openServiceProjects",
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setOpenServiceProjects(response.data.data);
            }
            setLoading(false);
        }).catch(error => {
            console.error("Error loading open service projects:", error);
            setLoading(false);
        });
    };

    const sendNotifications = () => {
        if (!confirm(`Möchten Sie wirklich Benachrichtigungen für ${openServiceProjects.length} offene(n) Service-Auftrag/Aufträge senden?\n\nE-Mails werden an die verantwortlichen Personen gesendet.\nkulach@unternehmen.online wird in CC gesetzt.`)) {
            return;
        }

        setSendingNotifications(true);
        setNotificationResult(null);

        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "invoice/sendServiceProjectNotifications",
            auth: Auth.getAuthObject()
        }).then(response => {
            if (response.data.success) {
                setNotificationResult({
                    success: true,
                    message: response.data.message,
                    details: response.data.details
                });
            } else {
                setNotificationResult({
                    success: false,
                    message: "Fehler: " + response.data.error
                });
            }
            setSendingNotifications(false);
        }).catch(error => {
            console.error("Error sending notifications:", error);
            setNotificationResult({
                success: false,
                message: "Fehler beim Senden der Benachrichtigungen: " + error.message
            });
            setSendingNotifications(false);
        });
    };

    // Check if user is administrator
    if (!CurrentUser.data || CurrentUser.data.role !== 'administrator') {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger">
                    <h4>Zugriff verweigert</h4>
                    <p>Sie benötigen Administratorrechte, um auf das Rechnungsmodul zugreifen zu können.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <WeClappCustomerSelector
                isOpen={showCustomerSelector}
                onClose={() => setShowCustomerSelector(false)}
                onSelect={handleCustomerSelect}
                title="WeClapp-Kunden für Rechnung auswählen"
            />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                    <i className="bi bi-receipt me-2 text-primary"></i>
                    Rechnungsmodul
                </h2>
                {activeTab === 'invoices' && (
                    <div className="btn-group btn-group-sm">
                        <button
                            className={`btn ${viewMode === 'cards' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('cards')}
                            title="Kartenansicht"
                        >
                            <i className="bi bi-grid-3x3-gap"></i>
                        </button>
                        <button
                            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => setViewMode('list')}
                            title="Listenansicht"
                        >
                            <i className="bi bi-list-ul"></i>
                        </button>
                    </div>
                )}
            </div>

            {/* Tab Navigation */}
            <ul className="nav nav-tabs">
                <li className="nav-item">
                    <a 
                        className={`nav-link ${activeTab === 'invoices' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('invoices');
                        }}
                    >
                        Rechnungen
                    </a>
                </li>
                <li className="nav-item">
                    <a 
                        className={`nav-link ${activeTab === 'service' ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('service');
                        }}
                    >
                        Offene Service-Aufträge
                    </a>
                </li>
            </ul>
            
            {loading && <div className="mt-4">Lade {activeTab === 'invoices' ? 'Rechnungen' : 'Service-Aufträge'}...</div>}
            
            {!loading && !showDetail && activeTab === 'invoices' && (
                <div className="mt-4">
                    {/* Filter Toolbar */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-white border-bottom">
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 className="mb-0">
                                    <i className="bi bi-funnel me-2 text-primary"></i>
                                    Filter
                                </h5>
                                <button
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={resetFilters}
                                    title="Alle Filter zurücksetzen"
                                >
                                    <i className="bi bi-x-circle me-1"></i>
                                    Zurücksetzen
                                </button>
                            </div>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                {/* Description/Customer Filter */}
                                <div className="col-lg-4 col-md-6">
                                    <label className="form-label fw-semibold small text-muted">
                                        <i className="bi bi-search me-1"></i>
                                        Beschreibung / Kunde
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-file-text text-muted"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control border-start-0 ps-2"
                                            placeholder="Suchen..."
                                            value={filterDescription}
                                            onChange={e => setFilterDescription(e.target.value)}
                                        />
                                        {filterDescription && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={() => setFilterDescription('')}
                                            >
                                                <i className="bi bi-x"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Status Filter */}
                                <div className="col-lg-4 col-md-6">
                                    <label className="form-label fw-semibold small text-muted">
                                        <i className="bi bi-check-circle me-1"></i>
                                        Status
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-toggle-on text-muted"></i>
                                        </span>
                                        <select
                                            value={filterStatus}
                                            className="form-select border-start-0 ps-2"
                                            onChange={e => setFilterStatus(e.target.value)}
                                        >
                                            <option value="">Alle Status</option>
                                            <option value="local">Lokal</option>
                                            <option value="weclapp">WeClapp</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Creator Filter */}
                                <div className="col-lg-4 col-md-6">
                                    <label className="form-label fw-semibold small text-muted">
                                        <i className="bi bi-person me-1"></i>
                                        Erstellt von
                                    </label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">
                                            <i className="bi bi-person-circle text-muted"></i>
                                        </span>
                                        <select
                                            value={filterCreator}
                                            className="form-select border-start-0 ps-2"
                                            onChange={e => setFilterCreator(e.target.value)}
                                        >
                                            <option value="">Alle Ersteller</option>
                                            {uniqueCreators.map(creator => (
                                                <option key={creator} value={creator}>
                                                    {getRealNameFromUsername(creator)}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Active Filters Display */}
                            {(filterDescription || filterStatus || filterCreator || customerFilter) && (
                                <div className="mt-3 pt-3 border-top">
                                    <small className="text-muted fw-semibold me-2">Aktive Filter:</small>
                                    {filterDescription && (
                                        <span className="badge bg-primary me-2">
                                            <i className="bi bi-search me-1"></i>
                                            {filterDescription}
                                            <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterDescription('')}></i>
                                        </span>
                                    )}
                                    {filterStatus && (
                                        <span className="badge bg-info me-2">
                                            <i className="bi bi-toggle-on me-1"></i>
                                            {filterStatus === 'local' ? 'Lokal' : 'WeClapp'}
                                            <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterStatus('')}></i>
                                        </span>
                                    )}
                                    {filterCreator && (
                                        <span className="badge bg-warning me-2">
                                            <i className="bi bi-person me-1"></i>
                                            {getRealNameFromUsername(filterCreator)}
                                            <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setFilterCreator('')}></i>
                                        </span>
                                    )}
                                    {customerFilter && (
                                        <span className="badge bg-success me-2">
                                            <i className="bi bi-building me-1"></i>
                                            Kunde-Filter
                                            <i className="bi bi-x ms-1 cursor-pointer" onClick={() => setCustomerFilter(null)}></i>
                                        </span>
                                    )}
                                    <small className="text-muted">({filteredInvoices.length} Ergebnis{filteredInvoices.length !== 1 ? 'se' : ''})</small>
                                </div>
                            )}
                        </div>
                    </div>

                    {viewMode === 'cards' ? (
                        <div className="row">
                        {filteredInvoices.map(invoice => (
                            <div key={invoice.id} className="col-md-6 col-lg-4 mb-4">
                                <div className="card h-100 shadow-sm">
                                    <div className="card-header bg-light d-flex justify-content-between align-items-center">
                                        <div>
                                            <small className="text-muted">Rechnung #{invoice.id}</small>
                                            <div className="mt-1">
                                                {invoice.weclapp_invoice_id > 0 ? (
                                                    <span className="badge bg-success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        {invoice.weclapp_description || `ID: ${invoice.weclapp_invoice_id}`}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-warning">
                                                        <i className="bi bi-clock me-1"></i>
                                                        Lokal
                                                    </span>
                                                )}
                                                {invoice.email_send === 1 && (
                                                    <span className="badge bg-info ms-1">
                                                        <i className="bi bi-envelope-check"></i>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <h6 className="card-title text-truncate" title={invoice.description}>
                                            {invoice.description.length > 60
                                                ? invoice.description.substring(0, 60) + '...'
                                                : invoice.description}
                                        </h6>

                                        <div className="mt-3">
                                            {invoice.customer_name && (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <small className="text-muted">
                                                        <i className="bi bi-building me-1"></i>
                                                        Kunde
                                                    </small>
                                                    <strong title={invoice.customer_name}>
                                                        {invoice.customer_name.length > 25
                                                            ? invoice.customer_name.substring(0, 25) + '...'
                                                            : invoice.customer_name}
                                                    </strong>
                                                </div>
                                            )}

                                            {invoice.expense_entries && invoice.expense_entries.length > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <small className="text-muted">
                                                        <i className="bi bi-clock me-1"></i>
                                                        Zeiteinträge
                                                    </small>
                                                    <span className="badge bg-primary bg-opacity-10 text-primary">
                                                        {formatMinutes(invoice.expenses_duration_sum)}
                                                    </span>
                                                </div>
                                            )}

                                            {invoice.external_costs && invoice.external_costs.length > 0 && (
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <small className="text-muted">
                                                        <i className="bi bi-currency-euro me-1"></i>
                                                        Externe Kosten
                                                    </small>
                                                    <span className="badge bg-success bg-opacity-10 text-success">
                                                        {invoice.external_costs.reduce((sum, c) => sum + getExternalCostVK(c), 0).toFixed(2)} €
                                                    </span>
                                                </div>
                                            )}

                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <small className="text-muted">
                                                    <i className="bi bi-person me-1"></i>
                                                    Erstellt von
                                                </small>
                                                <span>{getRealNameFromUsername(invoice.created_by)}</span>
                                            </div>

                                            <div className="d-flex justify-content-between align-items-center">
                                                <small className="text-muted">
                                                    <i className="bi bi-calendar me-1"></i>
                                                    Erstellt am
                                                </small>
                                                <span>{moment(invoice.created_at).format("DD.MM.YY HH:mm")}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent d-flex gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-primary flex-fill"
                                            onClick={() => loadInvoiceDetail(invoice.id)}
                                        >
                                            <i className="bi bi-eye me-1"></i>
                                            Details
                                        </button>
                                        {invoice.weclapp_invoice_id > 0 && (
                                            <a
                                                className="btn btn-sm btn-outline-success"
                                                href={`https://unternehmenonline.weclapp.com/app/sales-invoice/${invoice.weclapp_invoice_id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="In WeClapp öffnen"
                                            >
                                                <i className="bi bi-box-arrow-up-right"></i>
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body p-0">
                                <table className="table mb-0 small table-hover">
                                    <thead className="table-light sticky-top">
                                        <tr>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-hash me-1"></i>
                                                ID
                                            </th>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-file-text me-1"></i>
                                                Beschreibung
                                            </th>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-building me-1"></i>
                                                Kunde
                                            </th>
                                            <th className="align-middle border-end text-center">
                                                <i className="bi bi-clock me-1"></i>
                                                Zeiteinträge
                                            </th>
                                            <th className="align-middle border-end text-center">
                                                <i className="bi bi-currency-euro me-1"></i>
                                                Externe Kosten
                                            </th>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-person me-1"></i>
                                                Erstellt von
                                            </th>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-calendar me-1"></i>
                                                Erstellt am
                                            </th>
                                            <th className="align-middle border-end">
                                                <i className="bi bi-toggle-on me-1"></i>
                                                Status
                                            </th>
                                            <th className="align-middle text-center" style={{width: '120px'}}>
                                                <i className="bi bi-gear me-1"></i>
                                                Aktionen
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvoices.map(invoice => (
                                            <tr key={invoice.id}>
                                                <td className="align-middle">
                                                    <span className="badge bg-light text-dark border">
                                                        #{invoice.id}
                                                    </span>
                                                </td>
                                                <td className="align-middle">
                                                    <strong>{invoice.description.length > 50
                                                        ? invoice.description.substring(0, 50) + '...'
                                                        : invoice.description}
                                                    </strong>
                                                </td>
                                                <td className="align-middle" title={invoice.customer_name || ''}>
                                                    {invoice.customer_name ? (
                                                        invoice.customer_name.length > 25
                                                            ? invoice.customer_name.substring(0, 25) + '...'
                                                            : invoice.customer_name
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {invoice.expense_entries && invoice.expense_entries.length > 0 ? (
                                                        <>
                                                            <span className="badge bg-primary bg-opacity-10 text-primary">
                                                                {formatMinutes(invoice.expenses_duration_sum)}
                                                            </span>
                                                            <div className="text-muted small">{invoice.expense_entries.length} Einträge</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {invoice.external_costs && invoice.external_costs.length > 0 ? (
                                                        <>
                                                            <span className="badge bg-success bg-opacity-10 text-success">
                                                                {invoice.external_costs.reduce((sum, c) => sum + getExternalCostVK(c), 0).toFixed(2)} €
                                                            </span>
                                                            <div className="text-muted small">{invoice.external_costs.length} Positionen</div>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="align-middle">{getRealNameFromUsername(invoice.created_by)}</td>
                                                <td className="align-middle">{moment(invoice.created_at).format("DD.MM.YY HH:mm")}</td>
                                                <td className="align-middle">
                                                    {invoice.weclapp_invoice_id > 0 ? (
                                                        <span className="badge bg-success">
                                                            <i className="bi bi-check-circle me-1"></i>
                                                            {invoice.weclapp_description || `ID: ${invoice.weclapp_invoice_id}`}
                                                        </span>
                                                    ) : (
                                                        <span className="badge bg-warning">
                                                            <i className="bi bi-clock me-1"></i>
                                                            Lokal
                                                        </span>
                                                    )}
                                                    {invoice.email_send === 1 && (
                                                        <span className="badge bg-info ms-1" title="E-Mail versendet">
                                                            <i className="bi bi-envelope-check"></i>
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            className="btn btn-outline-primary"
                                                            onClick={() => loadInvoiceDetail(invoice.id)}
                                                            title="Details"
                                                        >
                                                            <i className="bi bi-eye"></i>
                                                        </button>
                                                        {invoice.weclapp_invoice_id > 0 && (
                                                            <a
                                                                className="btn btn-outline-success"
                                                                href={`https://unternehmenonline.weclapp.com/app/sales-invoice/${invoice.weclapp_invoice_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                title="In WeClapp öffnen"
                                                            >
                                                                <i className="bi bi-box-arrow-up-right"></i>
                                                            </a>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {filteredInvoices.length === 0 && (
                        <div className="alert alert-info">
                            <i className="bi bi-info-circle me-2"></i>
                            {customerFilter ? 'Keine Rechnungen für diesen Kunden gefunden' : 'Keine Rechnungen vorhanden'}
                        </div>
                    )}
                </div>
            )}

            {!loading && activeTab === 'service' && (
                <div className="mt-4">
                    {/* Notification Result */}
                    {notificationResult && (
                        <div className={`alert ${notificationResult.success ? 'alert-success' : 'alert-danger'} alert-dismissible fade show mb-4`}>
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                    <h5 className="alert-heading mb-3">
                                        <i className={`bi ${notificationResult.success ? 'bi-check-circle' : 'bi-exclamation-triangle'} me-2`}></i>
                                        {notificationResult.success ? 'E-Mails erfolgreich versendet' : 'Fehler beim Versenden'}
                                    </h5>

                                    <p className="mb-3"><strong>{notificationResult.message}</strong></p>

                                    {notificationResult.success && notificationResult.details && (
                                        <>
                                            {notificationResult.details.sentTo && notificationResult.details.sentTo.length > 0 && (
                                                <div className="mb-3">
                                                    <h6 className="fw-bold mb-2">
                                                        <i className="bi bi-envelope-check me-1"></i>
                                                        Versendete E-Mails:
                                                    </h6>
                                                    <ul className="list-unstyled mb-0">
                                                        {notificationResult.details.sentTo.map((item, index) => (
                                                            <li key={index} className="mb-2">
                                                                <div className="d-flex align-items-start">
                                                                    <i className="bi bi-arrow-right-circle text-success me-2 mt-1"></i>
                                                                    <div>
                                                                        <strong>{item.project}</strong>
                                                                        <br />
                                                                        <small className="text-muted">
                                                                            An: {item.to}
                                                                            {item.cc !== '-' && ` | CC: ${item.cc}`}
                                                                            {' '}(Verantwortlich: {item.responsible})
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {notificationResult.details.errors && notificationResult.details.errors.length > 0 && (
                                                <div className="mb-3">
                                                    <h6 className="fw-bold mb-2 text-warning">
                                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                                        Warnungen:
                                                    </h6>
                                                    <ul className="mb-0">
                                                        {notificationResult.details.errors.map((error, index) => (
                                                            <li key={index} className="text-warning small">{error}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setNotificationResult(null)}
                                    aria-label="Close"
                                ></button>
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h5 className="mb-0">Offene Service-Aufträge</h5>
                            <small className="text-muted">
                                {openServiceProjects.length} Auftrag{openServiceProjects.length !== 1 ? 'e' : ''} |
                                {' '}{formatMinutes(openServiceProjects.reduce((sum, p) => sum + (parseInt(p.open_minutes) || 0), 0))} |
                                {' '}{new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((openServiceProjects.reduce((sum, p) => sum + (parseInt(p.open_minutes) || 0), 0) / 60) * 110)} €
                            </small>
                        </div>
                        <button
                            className="btn btn-primary"
                            onClick={sendNotifications}
                            disabled={sendingNotifications || openServiceProjects.length === 0}
                        >
                            {sendingNotifications ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Sende E-Mails...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-envelope me-2"></i>
                                    Benachrichtigungen senden
                                </>
                            )}
                        </button>
                    </div>

                    <div className="card shadow-sm">
                        <div className="card-body p-0">
                            <table className="table mb-0 small table-hover">
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th className="align-middle border-end">
                                            <i className="bi bi-briefcase me-1"></i>
                                            Service-Auftrag
                                        </th>
                                        <th className="align-middle border-end">
                                            <i className="bi bi-building me-1"></i>
                                            Kunde
                                        </th>
                                        <th className="align-middle border-end">
                                            <i className="bi bi-person me-1"></i>
                                            Verantwortlich
                                        </th>
                                        <th className="align-middle border-end text-end">
                                            <i className="bi bi-clock me-1"></i>
                                            Offene Minuten
                                        </th>
                                        <th className="align-middle border-end text-end">
                                            <i className="bi bi-currency-euro me-1"></i>
                                            Geschätzter Betrag
                                        </th>
                                        <th className="align-middle border-end text-center">
                                            <i className="bi bi-hash me-1"></i>
                                            Einträge
                                        </th>
                                        <th className="align-middle text-center">
                                            <i className="bi bi-calendar-range me-1"></i>
                                            Zeitraum
                                        </th>
                                        <th className="align-middle text-center" style={{width: '100px'}}>
                                            <i className="bi bi-gear me-1"></i>
                                            Aktionen
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {openServiceProjects.map(project => (
                                        <tr key={project.id}>
                                            <td className="align-middle">
                                                <strong>{project.title}</strong>
                                            </td>
                                            <td className="align-middle" title={project.customer_name || ''}>
                                                {project.customer_name ? (
                                                    project.customer_name.length > 25
                                                        ? project.customer_name.substring(0, 25) + '...'
                                                        : project.customer_name
                                                ) : (
                                                    <span className="text-muted">N/A</span>
                                                )}
                                            </td>
                                            <td className="align-middle">
                                                {getRealNameFromUsername(project.portalowner)}
                                            </td>
                                            <td className="align-middle text-end">
                                                <span className="badge bg-light text-dark border">
                                                    {formatMinutes(parseInt(project.open_minutes) || 0)}
                                                </span>
                                            </td>
                                            <td className="align-middle text-end">
                                                <span className="badge bg-success" title="Geschätzter Betrag bei 110,00 EUR/Stunde. Slotify kennt keine kund:innenspezifische Preise.">
                                                    {new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((parseInt(project.open_minutes) / 60) * 110)} €
                                                    <i className="bi bi-info-circle ms-1"></i>
                                                </span>
                                            </td>
                                            <td className="align-middle text-center">
                                                <span className="badge bg-light text-dark border">
                                                    {project.entry_count}
                                                </span>
                                            </td>
                                            <td className="align-middle text-center text-nowrap">
                                                <small>
                                                    {project.date_from ? moment(project.date_from).format("DD.MM.YY") : ''}
                                                    {' '}-{' '}
                                                    {project.date_to ? moment(project.date_to).format("DD.MM.YY") : ''}
                                                </small>
                                            </td>
                                            <td className="align-middle text-center">
                                                <button
                                                    onClick={() => history.push(`/expenses/${project.id}`)}
                                                    className="btn btn-sm btn-outline-info"
                                                    title="Zum Expense-Projekt springen"
                                                >
                                                    <i className="bi bi-arrow-right-circle"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="table-light">
                                    <tr className="fw-bold">
                                        <td colSpan="3" className="align-middle">
                                            <i className="bi bi-calculator me-1"></i>
                                            Gesamt
                                        </td>
                                        <td className="align-middle text-end">
                                            <span className="badge bg-primary">
                                                {formatMinutes(openServiceProjects.reduce((sum, p) => sum + (parseInt(p.open_minutes) || 0), 0))}
                                            </span>
                                        </td>
                                        <td className="align-middle text-end">
                                            <span className="badge bg-success">
                                                {new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((openServiceProjects.reduce((sum, p) => sum + (parseInt(p.open_minutes) || 0), 0) / 60) * 110)} €
                                            </span>
                                        </td>
                                        <td className="align-middle text-center">
                                            <span className="badge bg-light text-dark border">
                                                {openServiceProjects.reduce((sum, p) => sum + (parseInt(p.entry_count) || 0), 0)}
                                            </span>
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>

                    {openServiceProjects.length === 0 && (
                        <div className="alert alert-info mt-3">
                            <i className="bi bi-info-circle me-2"></i>
                            Keine offenen Service-Aufträge vorhanden
                        </div>
                    )}
                </div>
            )}

            {showDetail && selectedInvoice && (
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => setShowDetail(false)}
                        >
                            <i className="bi bi-arrow-left me-2"></i>
                            Zurück zur Übersicht
                        </button>
                        
                        <button
                            className="btn btn-outline-danger"
                            onClick={() => setShowDeleteConfirm(true)}
                        >
                            <i className="bi bi-trash me-2"></i>
                            Rechnung löschen
                        </button>
                    </div>

                    {/* Header Card - Modern Design */}
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start mb-4">
                                <div>
                                    <h2 className="mb-2">
                                        <i className="bi bi-receipt-cutoff me-2 text-primary"></i>
                                        Rechnung #{selectedInvoice.id}
                                    </h2>
                                    {selectedInvoice.customer_name && (
                                        <div className="d-flex align-items-center">
                                            <i className="bi bi-building text-muted me-2"></i>
                                            <span className="fs-5 text-dark fw-semibold">{selectedInvoice.customer_name}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="text-end">
                                    {selectedInvoice.weclapp_invoice_id > 0 ? (
                                        <span className="badge bg-success fs-6 px-3 py-2">
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                            {selectedInvoice.weclapp_description || 'In WeClapp'}
                                        </span>
                                    ) : (
                                        <span className="badge bg-warning text-dark fs-6 px-3 py-2">
                                            <i className="bi bi-clock-history me-2"></i>
                                            Lokal
                                        </span>
                                    )}
                                    {selectedInvoice.email_send === 1 && (
                                        <span className="badge bg-info fs-6 px-3 py-2 ms-2">
                                            <i className="bi bi-envelope-check-fill me-2"></i>
                                            E-Mail versendet
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="row g-4">
                                {selectedInvoice.expense_entries && selectedInvoice.expense_entries.length > 0 && (
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center">
                                            <div className="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                                                <i className="bi bi-clock-history text-primary fs-4"></i>
                                            </div>
                                            <div>
                                                <div className="text-muted small">Zeiteinträge</div>
                                                <div className="fs-5 fw-bold text-primary">{formatMinutes(selectedInvoice.expenses_duration_sum)}</div>
                                                <div className="text-muted small">{selectedInvoice.expense_entries.length} Einträge</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {selectedInvoice.external_costs && selectedInvoice.external_costs.length > 0 && (
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center">
                                            <div className="rounded-circle bg-success bg-opacity-10 p-3 me-3">
                                                <i className="bi bi-currency-euro text-success fs-4"></i>
                                            </div>
                                            <div>
                                                <div className="text-muted small">Externe Kosten</div>
                                                <div className="fs-5 fw-bold text-success">
                                                    {selectedInvoice.external_costs.reduce((sum, c) => sum + getExternalCostVK(c), 0).toFixed(2)} €
                                                </div>
                                                <div className="text-muted small">{selectedInvoice.external_costs.length} Positionen</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="col-md-3">
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                                            <i className="bi bi-calendar-check text-info fs-4"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Erstellt am</div>
                                            <div className="fw-semibold">{moment(selectedInvoice.created_at).format("DD.MM.YYYY")}</div>
                                            <div className="text-muted small">{moment(selectedInvoice.created_at).format("HH:mm")} Uhr</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="d-flex align-items-center">
                                        <div className="rounded-circle bg-secondary bg-opacity-10 p-3 me-3">
                                            <i className="bi bi-person-circle text-secondary fs-4"></i>
                                        </div>
                                        <div>
                                            <div className="text-muted small">Erstellt von</div>
                                            <div className="fw-semibold">{getRealNameFromUsername(selectedInvoice.created_by)}</div>
                                        </div>
                                    </div>
                                </div>
                                {selectedInvoice.weclapp_invoice_id > 0 && selectedInvoice.weclapp_status && (
                                    <div className="col-md-3">
                                        <div className="d-flex align-items-center">
                                            <div className="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                                                <i className="bi bi-cloud-check text-warning fs-4"></i>
                                            </div>
                                            <div>
                                                <div className="text-muted small">WeClapp Status</div>
                                                <div className="fw-semibold">{selectedInvoice.weclapp_status}</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Description Card */}
                    <div className="card shadow-sm border-0 mb-4">
                        <div className="card-body p-4">
                            <h5 className="mb-3 d-flex align-items-center">
                                <i className="bi bi-file-text me-2 text-primary"></i>
                                Beschreibung
                            </h5>
                            <div className="bg-light p-3 rounded-3 border">
                                <pre className="mb-0" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit'}}>{selectedInvoice.description}</pre>
                            </div>
                        </div>
                    </div>

                    {/* Expense Entries Card */}
                    {selectedInvoice.expense_entries && selectedInvoice.expense_entries.length > 0 && (
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body p-4">
                                <h5 className="mb-4 d-flex align-items-center justify-content-between">
                                    <span>
                                        <i className="bi bi-list-check me-2 text-primary"></i>
                                        Zeiteinträge
                                    </span>
                                    <span className="badge bg-primary rounded-pill">{selectedInvoice.expense_entries.length}</span>
                                </h5>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr className="border-bottom">
                                                <th className="text-muted fw-normal small pb-3">Datum</th>
                                                <th className="text-muted fw-normal small pb-3">Tätigkeit</th>
                                                <th className="text-muted fw-normal small pb-3">Projekt</th>
                                                <th className="text-muted fw-normal small pb-3">Kommentar</th>
                                                <th className="text-muted fw-normal small pb-3">Person</th>
                                                <th className="text-end text-muted fw-normal small pb-3">Dauer</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedInvoice.expense_entries.map((entry, index) => (
                                                <tr key={entry.id} className={index % 2 === 0 ? 'bg-light bg-opacity-50' : ''}>
                                                    <td className="py-3">
                                                        <i className="bi bi-calendar3 me-2 text-muted"></i>
                                                        <span className="text-nowrap">{moment(entry.date).format("DD.MM.YY")}</span>
                                                    </td>
                                                    <td className="py-3">
                                                        <span className="badge rounded-pill bg-secondary bg-opacity-75">
                                                            {entry.activity || 'Development'}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 small text-muted">#{entry.expense_project_id}</td>
                                                    <td className="py-3">{entry.comment}</td>
                                                    <td className="py-3">{getRealNameFromUsername(entry.person)}</td>
                                                    <td className="text-end py-3">
                                                        <span className="badge bg-primary bg-opacity-10 text-primary fw-semibold px-3 py-2">
                                                            {formatMinutes(entry.minutes)}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="border-top mt-3 pt-3">
                                    <div className="row">
                                        <div className="col text-end">
                                            <span className="text-muted me-3">Gesamtdauer:</span>
                                            <span className="fs-4 fw-bold text-primary">
                                                {formatMinutes(selectedInvoice.expense_entries.reduce((sum, e) => sum + e.minutes, 0))}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* External Costs Card */}
                    {selectedInvoice.external_costs && selectedInvoice.external_costs.length > 0 && (
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-body p-4">
                                <h5 className="mb-4 d-flex align-items-center justify-content-between">
                                    <span>
                                        <i className="bi bi-currency-euro me-2 text-success"></i>
                                        Externe Kosten
                                    </span>
                                    <span className="badge bg-success rounded-pill">{selectedInvoice.external_costs.length}</span>
                                </h5>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead>
                                            <tr className="border-bottom">
                                                <th className="text-muted fw-normal small pb-3">Datum</th>
                                                <th className="text-muted fw-normal small pb-3">Beschreibung</th>
                                                <th className="text-muted fw-normal small pb-3">Person</th>
                                                <th className="text-muted fw-normal small pb-3">Rechnung</th>
                                                <th className="text-end text-muted fw-normal small pb-3">Betrag</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedInvoice.external_costs.map((cost, index) => (
                                                <tr key={cost.id} className={index % 2 === 0 ? 'bg-light bg-opacity-50' : ''}>
                                                    <td className="py-3">
                                                        <i className="bi bi-calendar3 me-2 text-muted"></i>
                                                        <span className="text-nowrap">{moment(cost.date).format("DD.MM.YY")}</span>
                                                    </td>
                                                    <td className="py-3">{cost.description}</td>
                                                    <td className="py-3">{getRealNameFromUsername(cost.person)}</td>
                                                    <td className="py-3">
                                                        {cost.invoice_number ? (
                                                            <span className="badge bg-info bg-opacity-10 text-info">
                                                                {cost.invoice_number}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-end py-3">
                                                        <span className="badge bg-success bg-opacity-10 text-success fw-semibold px-3 py-2">
                                                            {getExternalCostVK(cost).toFixed(2)} €
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="border-top mt-3 pt-3">
                                    <div className="row">
                                        <div className="col text-end">
                                            <span className="text-muted me-3">Gesamtbetrag:</span>
                                            <span className="fs-4 fw-bold text-success">
                                                {selectedInvoice.external_costs.reduce((sum, c) => sum + getExternalCostVK(c), 0).toFixed(2)} €
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* WeClapp Actions Card */}
                    {selectedInvoice.weclapp_invoice_id > 0 ? (
                        <div className="card shadow-sm border-0 border-start border-success border-5">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="mb-2">
                                            <i className="bi bi-check-circle-fill text-success me-2"></i>
                                            In WeClapp vorhanden
                                        </h5>
                                        <p className="mb-1 text-muted">
                                            <strong>Rechnungsnummer:</strong> {selectedInvoice.weclapp_description}
                                        </p>
                                        <p className="mb-0 text-muted small">
                                            WeClapp-ID: {selectedInvoice.weclapp_invoice_id}
                                        </p>
                                    </div>
                                    <a
                                        className="btn btn-success btn-lg"
                                        href={`https://unternehmenonline.weclapp.com/app/sales-invoice/${selectedInvoice.weclapp_invoice_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <i className="bi bi-box-arrow-up-right me-2"></i>
                                        In WeClapp öffnen
                                    </a>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm border-0">
                            <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">
                                    <i className="bi bi-cloud-upload me-2"></i>
                                    An WeClapp senden
                                </h5>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">
                                        <i className="bi bi-building me-2"></i>
                                        WeClapp Kunden-ID
                                    </label>
                                    <div className="input-group">
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={weclappCustomerId}
                                            onChange={(e) => setWeclappCustomerId(e.target.value)}
                                            placeholder="z.B. 5663644"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary"
                                            onClick={() => setShowCustomerSelector(true)}
                                            title="WeClapp-Kunden auswählen"
                                        >
                                            <i className="bi bi-search me-1"></i>
                                            Suchen
                                        </button>
                                    </div>
                                    {weclappCustomerName && (
                                        <div className="alert alert-success d-flex align-items-center mt-2 py-2 mb-0" role="alert">
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                            <small className="mb-0">
                                                Kunden-ID von <strong>"{weclappCustomerName}"</strong> übernommen
                                            </small>
                                        </div>
                                    )}
                                    {!weclappCustomerName && (
                                        <small className="form-text text-muted d-block mt-2">
                                            <i className="bi bi-info-circle me-1"></i>
                                            Geben Sie die Kunden-ID ein oder nutzen Sie die Suche
                                        </small>
                                    )}
                                </div>
                                
                                <button
                                    className="btn btn-primary btn-lg w-100"
                                    onClick={sendToWeClapp}
                                    disabled={!weclappCustomerId}
                                >
                                    <i className="bi bi-send-fill me-2"></i>
                                    Jetzt an WeClapp senden
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <SlideToConfirm
                show={showDeleteConfirm}
                onConfirm={deleteInvoice}
                onCancel={() => setShowDeleteConfirm(false)}
                title="Rechnung wirklich löschen?"
                message={`ACHTUNG: Das Löschen der Rechnung #${selectedInvoice?.id || ''} ist endgültig und kann nicht rückgängig gemacht werden! Alle ${selectedInvoice?.expense_entries?.length || 0} Zeiteinträge werden wieder freigegeben und können erneut abgerechnet werden.`}
                slideText="Zum Löschen schieben"
                variant="danger"
            />
        </div>
    );
};

export default InvoicesPage;